/**
 * 픽업 카트 네비게이션 컴포넌트
 */
import { Z_INDEX } from '@/constants/zIndex';
import type { BottomNavigationTranslations } from './constants';

interface PickupCartNavProps {
  cartTotal: string;
  cartItemCount: number;
  mode: 'pickup' | 'table';
  orderedItemCount: number;
  onCartClick?: () => void;
  onPaymentClick?: () => void;
  t: BottomNavigationTranslations;
}

export function PickupCartNav({
  cartTotal,
  cartItemCount,
  mode,
  orderedItemCount,
  onCartClick,
  onPaymentClick,
  t,
}: PickupCartNavProps) {
  // 테이블 주문 모드 동작 로직:
  // 1. orderedItemCount === 0 && cartItemCount === 0: 비활성화 (클릭 불가)
  // 2. cartItemCount > 0: 장바구니 모달 열기
  // 3. orderedItemCount > 0 && cartItemCount === 0: 결제 페이지로 이동

  // 픽업 모드: cartItemCount > 0일 때만 활성화
  const isDisabled = mode === 'table'
    ? (orderedItemCount === 0 && cartItemCount === 0)
    : (cartItemCount === 0);

  const handleButtonClick = () => {
    if (isDisabled) return;

    if (mode === 'table') {
      // 테이블 주문 모드
      if (cartItemCount > 0) {
        // 장바구니에 아이템이 있으면 장바구니 모달 열기
        onCartClick?.();
      } else if (orderedItemCount > 0 && onPaymentClick) {
        // 주문 완료된 아이템만 있으면 결제 페이지로 이동
        onPaymentClick();
      }
    } else {
      // 픽업 모드: 장바구니 모달 열기
      onCartClick?.();
    }
  };

  const getButtonLabel = () => {
    if (mode === 'table' && orderedItemCount > 0 && cartItemCount === 0) {
      return t.pay;
    }
    return t.cart;
  };

  // #634: 테이블 모드 + 빈 장바구니에서는 이 fixed 네비(z-50)가 후불 세션 진행바
  // (SessionProgressBar)의 결제 CTA 와 시트 "전체 결제하기"를 가린다. 이 상태의 네비는
  //  - 담은 것 없음(cart=0) → 장바구니 진입 불필요
  //  - 시킨 것 있어도(ordered>0) 결제는 진행바→ChargeSessionModal 이 담당(구형 /checkout 경로 중복)
  // 이므로 렌더하지 않아 진행바가 하단을 온전히 점유하게 한다(이중 결제 경로 + occlusion 동시 제거).
  // 장바구니에 담는 중(cart>0)이면 주문 동선을 위해 그대로 노출하고, 픽업 모드는 기존 동작 유지.
  if (mode === 'table' && cartItemCount === 0) {
    return null;
  }

  return (
    // data-bottom-nav: GlobalTableSessionBar(#660)가 네비 실측 높이로 bottom offset 을
    // 계산하는 anchor 속성 (TabNav/PickupCartNav 공통).
    <nav data-bottom-nav className="fixed bottom-0 left-0 right-0 bg-background-secondary" style={{ zIndex: Z_INDEX.BOTTOM_NAVIGATION }}>
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={isDisabled}
        aria-label={`${cartTotal} ${getButtonLabel()} ${cartItemCount}`}
        className={`flex flex-row items-center relative size-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
          isDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-background-secondary/80'
        }`}
      >
        <div className="box-border content-stretch flex items-center justify-between px-[clamp(16px,2.86vw,20px)] py-0 relative w-full h-[clamp(50px,8.57vw,60px)] min-h-[clamp(50px,8.57vw,60px)]">
          <p className={`font-['Noto_Sans:Medium',_sans-serif] font-medium leading-[normal] relative shrink-0 text-[clamp(18px,3.43vw,24px)] text-nowrap tracking-[-0.48px] whitespace-pre transition-all duration-200 ${
            isDisabled ? 'text-muted-foreground opacity-40' : 'text-primary'
          }`} style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
            {cartTotal}
          </p>
          <span
            className={`flex flex-col font-['Noto_Sans:Medium',_'Noto_Sans_KR:Medium',_sans-serif] font-medium justify-center leading-[0] relative shrink-0 text-[clamp(18px,3.43vw,24px)] text-center text-nowrap tracking-[-0.48px] transition-all duration-200 ${
              isDisabled
                ? 'text-muted-foreground opacity-40'
                : 'text-foreground'
            }`}
            style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
          >
            <span className="leading-[normal] whitespace-pre uppercase">
              {getButtonLabel()}
            </span>
          </span>
          <span
            className={`font-['Noto_Sans:Bold',_sans-serif] font-bold leading-[normal] relative shrink-0 text-[clamp(16px,3.14vw,22px)] text-center text-nowrap tracking-[-0.44px] transition-all duration-200 ${
              isDisabled ? 'text-muted-foreground opacity-40' : 'text-primary'
            }`}
            style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
          >
            {cartItemCount}
          </span>
        </div>
      </button>
    </nav>
  );
}
